import React, {
 createContext, useContext, useState, useCallback, useMemo, useEffect
} from 'react';
import moment, { Moment } from 'moment';
import { useParams, useHistory } from 'react-router';
import {
  CalendarContextType,
  CalendarEvent,
  EEventFilterTypes,
  EEventStatus,
  EEventTypes,
  ExchangeEvent,
} from '../types/events';
import { fetchCalendarEvents } from '../actions/fetch-events';
import { useTranslation } from 'react-i18next';
import calendarEventManager from '../utils/calendar-event-manager';

// Context
const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

// Helper functions for URL synchronization
const parseWeekFromUrl = (weekParam?: string): Moment => {
  if (weekParam) {
    const parsed = moment(weekParam, 'YYYY-MM-DD');
    if (parsed.isValid()) {
      return parsed.startOf('isoWeek');
    }
  }
  return moment().startOf('isoWeek');
};

const formatWeekForUrl = (week: Moment): string => {
  return week.format('YYYY-MM-DD');
};

// Provider component
interface CalendarProviderProps {
  children: React.ReactNode;
}

export function CalendarProvider({ children }: CalendarProviderProps) {
  const { week: weekParam } = useParams<{ week?: string }>();
  const history = useHistory();
  const { t } = useTranslation();

  // Initialize currentWeek from URL parameter
  const [currentWeek, setCurrentWeek] = useState<Moment>(() => parseWeekFromUrl(weekParam));
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [activeFilters, setActiveFilters] = useState<EEventFilterTypes[]>([]);
  const [statusFilter, setStatusFilter] = useState<EEventStatus | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchSchedules, setRefetchSchedules] = useState<boolean>(false);

  useEffect(() => {
    const weekFromUrl = parseWeekFromUrl(weekParam);
    if (!weekFromUrl.isSame(currentWeek, 'day')) {
      setCurrentWeek(weekFromUrl);
    }
  }, [weekParam, currentWeek]);

  const filteredEvents = useMemo(() => {
    return events.filter((event: CalendarEvent) => {
      const eventFilterTypes = Array.isArray(event.filter_type)
        ? event.filter_type
        : [event.filter_type];

      const isExchange = event.type === EEventTypes.EXCHANGE;
      const eventStatus = isExchange ? (event as ExchangeEvent).status : null;

      if (activeFilters.length === 0) {
        if (!isExchange) {
          return true;
        }
        return statusFilter ? eventStatus === statusFilter : true;
      }

      const matchesFilterType = eventFilterTypes.some((type) => activeFilters.includes(type)
      );

      if (isExchange) {
        const matchesStatus = statusFilter ? eventStatus === statusFilter : true;
        return matchesFilterType && matchesStatus;
      }

      return matchesFilterType;
    });
  }, [events, activeFilters, statusFilter]);

  const updateUrlWithWeek = useCallback((week: Moment) => {
    const weekString = formatWeekForUrl(week);
    const currentPath = history.location.pathname;

    if (currentPath.includes('/admin/calendar')) {
      history.push(`/admin/calendar/${weekString}`);
    } else if (currentPath.includes('/networks/') && currentPath.includes('/calendar')) {
      const networkId = currentPath.split('/')[2];
      history.push(`/networks/${networkId}/calendar/${weekString}`);
    }
  }, [history]);

  const goToNextWeek = useCallback(() => {
    const nextWeek = moment(currentWeek).add(1, 'week').startOf('isoWeek');
    setCurrentWeek(nextWeek);
    updateUrlWithWeek(nextWeek);
  }, [currentWeek, updateUrlWithWeek]);

  const goToPreviousWeek = useCallback(() => {
    const prevWeek = moment(currentWeek).subtract(1, 'week').startOf('isoWeek');
    setCurrentWeek(prevWeek);
    updateUrlWithWeek(prevWeek);
  }, [currentWeek, updateUrlWithWeek]);

  const goToToday = useCallback(() => {
    const today = moment().startOf('isoWeek');
    setCurrentWeek(today);
    updateUrlWithWeek(today);
  }, [updateUrlWithWeek]);

  const toggleEventTypeFilter = useCallback((type: EEventFilterTypes[]) => {
    setActiveFilters(type);
  }, []);

  const clearFilters = useCallback(() => {
    setActiveFilters([]);
  }, []);

  const fetchEvents = useCallback(async (week: Moment, organisationId?: string, loggedUserId?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const fetchedEvents = await fetchCalendarEvents(week, organisationId, loggedUserId, t);
      setEvents(fetchedEvents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addEvent = useCallback((event: CalendarEvent) => {
    setEvents((prev) => [...prev, event]);
  }, []);

  const updateEvent = useCallback((eventId: string, updates: Partial<CalendarEvent>) => {
    setEvents((prev) => prev.map((event) => (event.id === eventId
          ? { ...event, ...updates } as CalendarEvent
          : event)
      )
    );
  }, []);

  const refetchSchedulesCallback = useCallback(() => {
    setRefetchSchedules(!refetchSchedules);
  }, [refetchSchedules]);

  const deleteEvent = useCallback((eventId: string) => {
    setEvents((prev) => prev.filter((event) => event.id !== eventId));
  }, []);

  // Register with global event manager
  useEffect(() => {
    calendarEventManager.registerCallbacks({
      updateEvent,
      deleteEvent,
      addEvent,
    });

    return () => {
      calendarEventManager.unregisterCallbacks();
    };
  }, [updateEvent, deleteEvent, addEvent]);

  // Memoized context value
  const contextValue = useMemo<CalendarContextType>(() => ({
    currentWeek,
    events,
    filteredEvents,
    activeFilters,
    isLoading,
    error,
    goToNextWeek,
    goToPreviousWeek,
    goToToday,
    setCurrentWeek,
    toggleEventTypeFilter,
    clearFilters,
    fetchEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    refetchSchedulesCallback,
    refetchSchedules,
    statusFilter,
    setStatusFilter
  }), [
    currentWeek,
    events,
    filteredEvents,
    activeFilters,
    isLoading,
    error,
    goToNextWeek,
    goToPreviousWeek,
    goToToday,
    toggleEventTypeFilter,
    clearFilters,
    fetchEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    refetchSchedulesCallback,
    refetchSchedules,
    statusFilter,
    setStatusFilter
  ]);

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
}

export function useCalendar(): CalendarContextType {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
}
